import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { motion } from 'framer-motion';
import { Search, Play, BookOpen, Calculator, Microscope, Sparkles } from 'lucide-react';
import { Game } from '@/types/games';
import { gameService } from '@/services/gameService';
import { useAuth } from '@/contexts/AuthContext';

interface GameSelectorProps {
  grade: string;
  subject?: string;
  onGameSelect: (game: Game) => void;
}

const GameSelector = ({ grade, subject, onGameSelect }: GameSelectorProps) => {
  const { user } = useAuth();
  const [games, setGames] = useState<Game[]>([]);
  const [filteredGames, setFilteredGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [difficultyFilter, setDifficultyFilter] = useState<string>('all');

  useEffect(() => {
    loadGames();
  }, [grade, subject]);

  useEffect(() => {
    filterGames();
  }, [games, searchTerm, typeFilter, difficultyFilter]);

  const loadGames = async () => {
    try {
      setLoading(true);
      console.log('GameSelector: Loading games for grade:', grade, 'subject:', subject);
      
      const gameData = await gameService.getGamesForGradeAndSubject(grade, subject);
      console.log('GameSelector: Raw game data from service:', gameData);
      
      // Debug each game with detailed logging
      gameData.forEach((game, index) => {
        console.log(`GameSelector: Game ${index + 1}:`, {
          id: game.id,
          name: game.name,
          game_type: game.game_type,
          game_config: game.game_config,
          gameType: game.game_config?.gameType,
          is_active: game.is_active,
          grade_min: game.grade_min,
          grade_max: game.grade_max
        });
      });
      
      // Check specifically for Math Runner and other games
      const mathRunner = gameData.find(game => 
        game.name.toLowerCase().includes('math runner') || 
        game.game_config?.gameType === 'mathRunner'
      );
      
      if (mathRunner) {
        console.log('GameSelector: Math Runner found!', {
          name: mathRunner.name,
          config: mathRunner.game_config,
          gameType: mathRunner.game_config?.gameType,
          active: mathRunner.is_active,
          gradeRange: `${mathRunner.grade_min}-${mathRunner.grade_max}`
        });
      } else {
        console.warn('GameSelector: Math Runner NOT found in games list');
        console.log('GameSelector: Available games:', gameData.map(g => ({ 
          name: g.name, 
          gameType: g.game_config?.gameType,
          gradeRange: `${g.grade_min}-${g.grade_max}`
        })));
      }
      
      setGames(gameData);
    } catch (error) {
      console.error('GameSelector: Error loading games:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterGames = () => {
    let filtered = [...games];
    console.log('GameSelector: Filtering games, starting with:', filtered.length);

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(game =>
        game.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        game.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      console.log('GameSelector: After search filter:', filtered.length);
    }

    // Filter by type
    if (typeFilter !== 'all') {
      filtered = filtered.filter(game => game.game_type === typeFilter);
      console.log('GameSelector: After type filter:', filtered.length);
    }

    // Filter by difficulty
    if (difficultyFilter !== 'all') {
      const difficulty = parseInt(difficultyFilter);
      filtered = filtered.filter(game => game.difficulty_levels?.includes(difficulty));
      console.log('GameSelector: After difficulty filter:', filtered.length);
    }

    console.log('GameSelector: Final filtered games:', filtered.map(g => ({
      name: g.name,
      gameType: g.game_config?.gameType
    })));
    setFilteredGames(filtered);
  };

  const getGameIcon = (gameType: string) => {
    switch (gameType) {
      case 'math': return <Calculator className="h-6 w-6" />;
      case 'reading': return <BookOpen className="h-6 w-6" />;
      case 'science': return <Microscope className="h-6 w-6" />;
      default: return <Sparkles className="h-6 w-6" />;
    }
  };

  const getTypeColor = (gameType: string) => {
    switch (gameType) {
      case 'math': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'reading': return 'bg-green-100 text-green-700 border-green-200';
      case 'science': return 'bg-purple-100 text-purple-700 border-purple-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'interactive': return 'bg-orange-100 text-orange-700';
      case 'puzzle': return 'bg-pink-100 text-pink-700';
      case 'simulation': return 'bg-cyan-100 text-cyan-700';
      case 'drill': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const handleGameSelect = (game: Game) => {
    console.log('GameSelector: Game selected!', {
      name: game.name,
      id: game.id,
      config: game.game_config,
      gameType: game.game_config?.gameType
    });
    onGameSelect(game);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Loading games...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl font-bold mb-4 text-gray-800">Choose Your Learning Game</h2>
          <p className="text-gray-600 mb-4">Interactive games designed for Grade {grade} curriculum</p>
          
          <Badge variant="secondary" className="text-lg px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold">
            Grade {grade} • {filteredGames.length} Games Available
          </Badge>
        </motion.div>
      </div>

      {/* Debug Info */}
      {process.env.NODE_ENV === 'development' && (
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4">
            <h4 className="font-semibold text-yellow-800 mb-2">Debug Info:</h4>
            <p className="text-sm text-yellow-700">Total games loaded: {games.length}</p>
            <p className="text-sm text-yellow-700">Filtered games: {filteredGames.length}</p>
            <p className="text-sm text-yellow-700">Grade: {grade}, Subject: {subject || 'all'}</p>
            <p className="text-sm text-yellow-700">User: {user ? 'Authenticated' : 'Not authenticated'}</p>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search games..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Game Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="math">Math</SelectItem>
                <SelectItem value="reading">Reading</SelectItem>
                <SelectItem value="science">Science</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={difficultyFilter} onValueChange={setDifficultyFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Difficulty" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="1">Level 1 (Easy)</SelectItem>
                <SelectItem value="2">Level 2 (Medium)</SelectItem>
                <SelectItem value="3">Level 3 (Hard)</SelectItem>
              </SelectContent>
            </Select>
            
            <Button
              onClick={() => {
                setSearchTerm('');
                setTypeFilter('all');
                setDifficultyFilter('all');
              }}
              variant="outline"
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Games Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredGames.map((game, index) => (
          <motion.div
            key={game.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card className={`h-full hover:shadow-lg transition-all duration-300 border-2 ${getTypeColor(game.game_type)} hover:scale-105`}>
              <CardHeader className="text-center pb-4">
                <div className="flex justify-center mb-3">
                  <div className={`p-3 rounded-full ${getTypeColor(game.game_type)} border-2`}>
                    {getGameIcon(game.game_type)}
                  </div>
                </div>
                <CardTitle className="text-xl text-gray-800 mb-2">{game.name}</CardTitle>
                <p className="text-gray-600 text-sm leading-relaxed">{game.description}</p>
                
                <div className="flex justify-center gap-2 mt-3">
                  <Badge variant="outline" className={getCategoryColor(game.game_category)}>
                    {game.game_category}
                  </Badge>
                  <Badge variant="outline" className="text-xs px-2 py-1">
                    ~{game.estimated_time}min
                  </Badge>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                {/* Learning Objectives Preview */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">You'll Learn:</h4>
                  <ul className="text-xs text-gray-600 space-y-1">
                    {game.learning_objectives?.slice(0, 3).map((objective, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="mr-1">•</span>
                        {objective}
                      </li>
                    )) || []}
                  </ul>
                </div>
                
                <Button
                  onClick={() => handleGameSelect(game)}
                  className="w-full bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white font-medium"
                  disabled={!user}
                >
                  <Play className="h-4 w-4 mr-2" />
                  {user ? 'Start Game' : 'Sign In to Play'}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {filteredGames.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500">No games found matching your criteria.</p>
          <Button
            onClick={() => {
              setSearchTerm('');
              setTypeFilter('all');
              setDifficultyFilter('all');
            }}
            variant="outline"
            className="mt-2"
          >
            Clear filters to see all games
          </Button>
        </div>
      )}
    </div>
  );
};

export default GameSelector;
